import { motion } from "framer-motion";
import { CheckCircle2, Sparkles } from "lucide-react";
import { BRAND } from "@/constants/brand";
import { useTheme } from "@/contexts/ThemeContext";

const highlights = [
  "Real market insights — no unnecessary hype",
  "Practical, easy-to-understand education",
  "Actionable strategies for every skill level",
  "Focus on disciplined, long-term wealth building",
];

const About = () => {
  const { theme } = useTheme();
  const bullBearImg = theme === "dark" ? BRAND.bullBearDark : BRAND.bullBearLight;

  return (
    <section id="about" className="section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          {/* Visual side — Bull vs Bear */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative lg:col-span-5"
          >
            <div className="relative overflow-hidden rounded-3xl border border-border bg-card/40 shadow-xl">
              <img
                key={theme}
                src={bullBearImg}
                alt="Bull versus Bear — the eternal battle of the markets"
                loading="lazy"
                decoding="async"
                className="h-[440px] w-full object-cover"
                data-testid="about-bullbear-image"
              />
              {/* Subtle gradient scrim only in dark mode for legibility of overlay chip */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 dark:opacity-80" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="glass inline-flex items-center gap-2 rounded-full px-4 py-2">
                  <Sparkles size={14} className="text-[hsl(var(--brand-orange))]" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-foreground">
                    Bull vs Bear • Master both cycles
                  </span>
                </div>
              </div>
            </div>

            {/* Floating stat */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="glass absolute -right-4 -bottom-6 rounded-2xl p-4 shadow-xl md:-right-8"
            >
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Focus</p>
              <p className="font-display text-lg font-bold">Education first,<br /> hype never.</p>
            </motion.div>
          </motion.div>

          {/* Text side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <span className="chip" data-testid="about-badge">About Buggu Capital</span>
            <h2
              className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
              data-testid="about-heading"
            >
              Simplifying finance,{" "}
              <span className="text-gradient-brand">one insight at a time.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              At Buggu Capital, our mission is to simplify the world of finance and help you make smarter investment decisions. Whether you&apos;re a beginner or an experienced investor, you&apos;ll find practical, easy-to-understand content designed to grow your financial knowledge.
            </p>

            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {highlights.map((h, i) => (
                <motion.li
                  key={h}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  className="flex items-start gap-3 rounded-xl border border-border bg-card/40 p-3"
                  data-testid={`about-highlight-${i}`}
                >
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[hsl(var(--brand-green))]" />
                  <span className="text-sm text-foreground/90">{h}</span>
                </motion.li>
              ))}
            </ul>

            <div className="mt-8 rounded-2xl border border-border bg-card/40 p-5">
              <p className="text-sm text-muted-foreground">
                Curated &amp; created by
              </p>
              <p className="mt-1 font-display text-lg font-bold" data-testid="founder-name">
                Chandan Kumar Sah <span className="text-[hsl(var(--brand-orange))]">•</span>{" "}
                <span className="text-muted-foreground font-medium">Founder, Buggu Capital</span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
